/**
 * WebSocket Infrastructure Types
 *
 * Core types for the WebSocket client and React hook.
 */

/**
 * Connection state machine.
 * - `connecting`: Initial connection in progress
 * - `connected`: Successfully connected
 * - `disconnected`: Disconnected (clean or intentional)
 * - `reconnecting`: Attempting to reconnect after unexpected close
 */
export type ConnectionState =
    | 'connecting'
    | 'connected'
    | 'disconnected'
    | 'reconnecting';

/**
 * Configuration for WebSocketClient.
 */
export interface WebSocketClientConfig {
    /** WebSocket URL */
    url: string;
    /** Optional subprotocols */
    protocols?: string[];
    /** Enable auto-reconnect (default: true) */
    reconnect?: boolean;
    /** Initial reconnect delay in ms (default: 1000) */
    reconnectInterval?: number;
    /** Max reconnect delay in ms (default: 30000) */
    maxReconnectInterval?: number;
    /** Max reconnect attempts, 0 = infinite (default: 0) */
    maxReconnectAttempts?: number;
}

/**
 * Default configuration values.
 */
export const DEFAULT_WS_CONFIG: Required<Omit<WebSocketClientConfig, 'url'>> = {
    protocols: [],
    reconnect: true,
    reconnectInterval: 1000,
    maxReconnectInterval: 30000,
    maxReconnectAttempts: 0,
};

/**
 * Callback for receiving messages.
 */
export type MessageCallback = (event: MessageEvent) => void;

/**
 * Callback for connection state changes.
 */
export type StateCallback = (state: ConnectionState) => void;

/**
 * Callback for error events.
 */
export type ErrorCallback = (error: Event) => void;

/**
 * Callback for close events.
 */
export type CloseCallback = (event: CloseEvent) => void;

/**
 * Options for the useWebSocket hook.
 */
export interface UseWebSocketOptions<T = MessageEvent> {
    /** Transform raw message to typed data */
    transform?: (event: MessageEvent) => T;
    /** Called on each message (after transform) */
    onMessage?: (data: T, event: MessageEvent) => void;
    /** Called on state change */
    onStateChange?: StateCallback;
    /** Called on error */
    onError?: ErrorCallback;
    /** Called on close */
    onClose?: CloseCallback;
    /** Auto-connect on mount (default: true) */
    autoConnect?: boolean;
    /** Enable auto-reconnect (default: true) */
    reconnect?: boolean;
    /** Initial reconnect delay in ms */
    reconnectInterval?: number;
    /** Max reconnect attempts, 0 = infinite */
    maxReconnectAttempts?: number;
}
